import { AnyAction } from "@reduxjs/toolkit";
import React, {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  useContext,
  useReducer,
} from "react";
import { ApplicationStateType, SuccessResponseType } from "../..";
import {
  FailureResponseType,
  MethodSetupRequest,
  MethodSetupResponse,
  RegistrationRequest,
  RegistrationResponse,
} from "../../types";
import { PaymentMethodCoordinatorJSClient } from "./clients";
import {
  useCreatePaymentMethod,
  useCreatePaymentMethodRegistration,
  useHandleClients,
  useHandleCscClient,
  useValidationRoutingNumber,
} from "./hooks";
import { PaymentsStateContextProvider } from "./payments-client-state-context";
import { initialState, paymentsClientSetupSlice } from "./state";
import { PaymentContextValue } from "../payment-context";
import {
  ValidationRoutingNumberRequest,
  ValidationRoutingNumberResponse,
} from "@ftdr/cscpayments-js-client/lib/types/types";
import { payment3_paymentmethodpb } from "@ftdr/payment3_paymentmethod_coordinator-js-client";
import { enTextTemplates } from "../../assets/i18n/en-text-templates";

export type PaymentsClientContextProps = {
  providerState?: PaymentsClientContextValue;
  children: ReactNode;
  applicationState: PaymentContextValue;
  cashFlow?: payment3_paymentmethodpb.CashFlow;
  localizedText: (text: keyof typeof enTextTemplates) => string;
};

export type CreatePaymentMethodResultType =
  | {
      result?: MethodSetupResponse;
      error?: FailureResponseType;
      success?: SuccessResponseType;
    }
  | undefined;

export type ValidationRoutingNumberResult = {
  result?: ValidationRoutingNumberResponse;
  error?: FailureResponseType;
};

export type ValidationRoutingNumber = (
  requestData: ValidationRoutingNumberRequest
) => Promise<ValidationRoutingNumberResult | undefined>;

export type PaymentsClientContextValue = {
  client?: PaymentMethodCoordinatorJSClient;
  createPaymentMethodRegistration: (
    requestData: RegistrationRequest,
    retryCount?: number
  ) => Promise<RegistrationResponse | undefined>;
  createPaymentMethod: (
    requestData: MethodSetupRequest
  ) => Promise<CreatePaymentMethodResultType>;
  dispatch: Dispatch<AnyAction>;
  applicationState: Pick<
    ApplicationStateType,
    | "onFailure"
    | "onSuccess"
    | "onResponse"
    | "applicationName"
    | "owner"
    | "formsSetup"
    | "testIdName"
    | "styleType"
    | "customerId"
  >;
  validationRoutingNumber: ValidationRoutingNumber;
  cashFlow?: payment3_paymentmethodpb.CashFlow;
};

export const PaymentsClientContextInitialState: PaymentsClientContextValue = {
  createPaymentMethodRegistration: () => Promise.resolve(undefined),
  createPaymentMethod: () => Promise.resolve(undefined),
  dispatch: () => undefined,
  applicationState: {
    onSuccess: () => undefined,
    onResponse: () => undefined,
    onFailure: () => undefined,
    applicationName: "",
  },
  validationRoutingNumber: () => Promise.resolve(undefined),
};

const PaymentClientContext = createContext<PaymentsClientContextValue>(
  PaymentsClientContextInitialState
);
export const { Provider: PaymentsClientContextProvider } = PaymentClientContext;

export const PaymentsClientContext: FC<PaymentsClientContextProps> = ({
  children,
  providerState,
  applicationState,
  cashFlow,
  localizedText,
}) => {
  const [internalState, dispatch] = useReducer(
    paymentsClientSetupSlice.reducer,
    initialState
  );

  const {
    token,
    baseURL,
    hostJsClient,
    onFailure,
    onResponse,
    onSuccess,
    applicationName,
    owner,
    state,
    setWalletCheckboxProps,
  } = { ...applicationState };

  const { paymentMethodCoordinatorClient } = useHandleClients({
    token,
    baseURL,
    hostJsClient,
  });

  const { cscPaymentsJsClient } = useHandleCscClient(
    baseURL || "",
    token || ""
  );

  const createPaymentMethodRegistration = useCreatePaymentMethodRegistration({
    dispatch,
    client: paymentMethodCoordinatorClient,
    onResponse,
    onFailure,
    applicationName,
    owner,
    state,
    localizedText,
    setWalletCheckboxProps,
  });
  const createPaymentMethod = useCreatePaymentMethod({
    dispatch,
    client: paymentMethodCoordinatorClient,
    uuid: internalState.paymentUuid,
    onFailure,
    onResponse,
    onSuccess,
    state,
    localizedText,
  });

  const { validationRoutingNumber } = useValidationRoutingNumber({
    dispatch,
    client: cscPaymentsJsClient,
    onFailure,
    onResponse,
    localizedText,
  });

  return (
    <PaymentsClientContextProvider
      value={
        providerState
          ? providerState
          : {
              client: paymentMethodCoordinatorClient,
              createPaymentMethodRegistration,
              createPaymentMethod,
              validationRoutingNumber,
              dispatch,
              applicationState,
              cashFlow,
            }
      }
    >
      <PaymentsStateContextProvider value={{ state: internalState }}>
        {children}
      </PaymentsStateContextProvider>
    </PaymentsClientContextProvider>
  );
};

export const usePaymentsClientContext = (): PaymentsClientContextValue =>
  useContext<PaymentsClientContextValue>(PaymentClientContext);
